import React, {useState, useEffect} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {format} from 'date-fns';
import {partOrderService} from '../../services/api';
import {toast} from "react-toastify";

const PartOrderDetails = () => {
	const { id } = useParams();
	const navigate = useNavigate();

	const [order, setOrder] = useState(null);
	const [loading, setLoading] = useState(true);
	const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
	const [receiveItems, setReceiveItems] = useState([]);
	const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
	const [confirmAction, setConfirmAction] = useState(null);
	const [confirmMessage, setConfirmMessage] = useState(null);
	const [confirmLoading, setConfirmLoading] = useState(false);

	// Add search state for receive items dialog
	const [searchTerm, setSearchTerm] = useState('');

	// Add search state for main order items table
	const [tableSearchTerm, setTableSearchTerm] = useState('');

	// Add sorting state
	const [sortConfig, setSortConfig] = useState({
		                                             key: null,
		                                             direction: 'ascending'
	                                             });

	useEffect(() => {
		const loadOrder = async () => {
			try {
				setLoading(true);
				const data = await partOrderService.getPartOrder(id);

				// Transform the raw API response to match the component's expected structure
				const transformedData = {
					id: data.id,
					orderNumber: data.order_number,
					vendor: data.vendor,
					status: data.status,
					order_date: data.order_date,
					expectedDate: data.expected_delivery_date,
					notes: data.notes,
					totalValue: parseFloat(data.total_value),
					paymentTerms: null, // Not in the response
					createdBy: data.created_by, // Not in the response
					items: data.items.map(item => ({
						id: item.id,
						quantity: item.quantity_ordered,
						quantity_received: item.quantity_received,
						unit_price: parseFloat(item.unit_price) || 0,
						part_type: item.part_type,
						part: {
							id: item.part_type.id,
							partNumber: item.part_type.part_identifier,
							description: item.part_type.name
						},
						product: item.product
					}))
				};

				setOrder(transformedData);

				// Initialize receive items structure for all order items
				if (transformedData.items) {
					setReceiveItems(transformedData.items.map(item => ({
						id: item.id,
						quantity: 0,
						tempEmpty: false, // Add tempEmpty flag for handling empty input state
						quantity_received: item.quantity_received || 0,
						quantity_ordered: item.quantity,
						partId: item.part.id,
						partNumber: item.part.partNumber,
						description: item.part.description,
						part_type: item.part_type,
						part: {
							id: item.part_type.id,
							partNumber: item.part_type.part_identifier,
							description: item.part_type.name
						},
						product: item.product
					})));
				}
			}
			catch (error) {
				console.error("Error fetching part order:", error);
				toast.error("Error fetching part order: " + error.message);
				// Alert or toast notification could go here
			}
			finally {
				setLoading(false);
			}
		};

		loadOrder();
	}, [id]);

	// Add sorting function
	const requestSort = (key) => {
		let direction = 'ascending';
		if (sortConfig.key === key && sortConfig.direction === 'ascending') {
			direction = 'descending';
		}
		setSortConfig({ key, direction });
	};

	// Function to get sorted and filtered items
	const getSortedAndFilteredItems = () => {
		if (!order || !order.items) return [];

		// First filter based on search term
		let filteredItems = [...order.items];

		if (tableSearchTerm.trim()) {
			const normalizedSearchTerm = tableSearchTerm.toLowerCase().trim();

			filteredItems = filteredItems.filter(item => {
				const partNumber = `${item.product?.sku || ''}-${item.part?.partNumber || ''}`.toLowerCase();
				const description = item.part?.description?.toLowerCase() || '';

				return partNumber.includes(normalizedSearchTerm) ||
				       description.includes(normalizedSearchTerm);
			});
		}

		// Then sort the filtered items
		if (sortConfig.key !== null) {
			filteredItems.sort((a, b) => {
				// Handle different data types based on the key
				let aValue, bValue;

				switch (sortConfig.key) {
					case 'partNumber':
						aValue = `${a.product.sku}-${a.part.partNumber}`.toLowerCase();
						bValue = `${b.product.sku}-${b.part.partNumber}`.toLowerCase();
						break;
					case 'description':
						aValue = a.part.description.toLowerCase();
						bValue = b.part.description.toLowerCase();
						break;
					case 'quantity':
						aValue = a.quantity;
						bValue = b.quantity;
						break;
					case 'received':
						aValue = a.quantity_received || 0;
						bValue = b.quantity_received || 0;
						break;
					case 'unit_price':
						aValue = a.unit_price;
						bValue = b.unit_price;
						break;
					case 'total':
						aValue = a.quantity * a.unit_price;
						bValue = b.quantity * b.unit_price;
						break;
					default:
						aValue = a[sortConfig.key];
						bValue = b[sortConfig.key];
				}

				if (aValue < bValue) {
					return sortConfig.direction === 'ascending' ? -1 : 1;
				}
				if (aValue > bValue) {
					return sortConfig.direction === 'ascending' ? 1 : -1;
				}
				return 0;
			});
		}
		return filteredItems;
	};

	// Function to render sort indicator
	const getSortIndicator = (key) => {
		if (sortConfig.key === key) {
			return sortConfig.direction === 'ascending' ? ' ↑' : ' ↓';
		}
		return '';
	};

	const handleStatusChange = async (newStatus) => {
		try {
			setConfirmLoading(true);
			await partOrderService.updatePartOrder(id, { status: newStatus });
			setOrder(prev => ({ ...prev, status: newStatus }));
			setConfirmLoading(false);
			setConfirmDialogOpen(false);
			// Success notification
			toast.success("Order status updated successfully");
		}
		catch (error) {
			console.error("Error updating status:", error);
			toast.error("Error updating order");

			// Error notification
		}
	};

	const handleReceiveItems = async () => {
		try {
			// Filter out items with quantity = 0
			const itemsToReceive = receiveItems.filter(item => item.quantity > 0);

			if (itemsToReceive.length === 0) {
				throw new Error('Please enter quantities for at least one item');
			}

			await partOrderService.receiveItems(id, itemsToReceive);

			// Refresh order data
			const updatedOrder = await partOrderService.getPartOrder(id);

			// Transform the updated data
			const transformedData = {
				id: updatedOrder.id,
				orderNumber: updatedOrder.order_number,
				vendor: updatedOrder.vendor,
				status: updatedOrder.status,
				order_date: updatedOrder.order_date,
				expectedDate: updatedOrder.expected_delivery_date,
				notes: updatedOrder.notes,
				totalValue: parseFloat(updatedOrder.total_value),
				paymentTerms: null,
				createdBy: null,
				items: updatedOrder.items.map(item => ({
					id: item.id,
					quantity: item.quantity_ordered,
					quantity_received: item.quantity_received,
					unit_price: parseFloat(item.unit_price) || 0,
					part_type:item.part_type,
					part: {
						id: item.part_type.id,
						partNumber: item.part_type.part_identifier,
						description: item.part_type.name
					},
					product: item.product
				}))
			};

			setOrder(transformedData);

			// Reset receive dialog
			setReceiveDialogOpen(false);
			setSearchTerm(''); // Reset search term
			setReceiveItems(transformedData.items.map(item => ({
				id: item.id,
				quantity: 0,
				tempEmpty: false, // Add tempEmpty flag for handling empty input state
				quantity_received: item.quantity_received || 0,
				quantity_ordered: item.quantity,
				partId: item.part.id,
				partNumber: item.part.partNumber,
				description: item.part.description,
				part: {
					id: item.part_type.id,
					partNumber: item.part_type.part_identifier,
					description: item.part_type.name
				},
				product: item.product
			})));

			// Success notification
			toast.success("Order items received successfully");
		}
		catch (error) {
			console.error("Error receiving items:", error);
			toast.error("Error receiving items: " + error.message);
			// Error notification
		}
	};

	const handleReceiveQuantityChange = (itemId, quantity) => {
		setReceiveItems(prev =>
			                prev.map(item =>
				                         item.id === itemId
				                         ? { ...item, quantity: Math.min(item.quantity_ordered - item.quantity_received, Math.max(0, quantity)) }
				                         : item
			                )
		);
	};

	const receiveAll = () => {
		setReceiveItems(prev =>
			                prev.map(item => ({
				                ...item,
				                quantity: item.quantity_ordered - item.quantity_received
			                }))
		);
	};

	// Filter receive items based on search term
	const getFilteredReceiveItems = () => {
		if (!searchTerm.trim()) {
			return receiveItems;
		}

		const normalizedSearchTerm = searchTerm.toLowerCase().trim();

		return receiveItems.filter(item => {
			const partNumber = `${item.product?.sku || ''}-${item.part?.partNumber || ''}`.toLowerCase();
			const description = item.part?.description?.toLowerCase() || '';

			return partNumber.includes(normalizedSearchTerm) ||
			       description.includes(normalizedSearchTerm);
		});
	};

	// Handle search term change for receive items dialog
	const handleSearchChange = (e) => {
		setSearchTerm(e.target.value);
	};

	// Handle search term change for main table
	const handleTableSearchChange = (e) => {
		setTableSearchTerm(e.target.value);
	};

	// Clear search for receive items dialog
	const clearSearch = () => {
		setSearchTerm('');
	};

	// Clear search for main table
	const clearTableSearch = () => {
		setTableSearchTerm('');
	};

	const renderStatusBadge = (status) => {
		const classes = {
			draft: 'bg-gray-100 text-gray-800',
			ordered: 'bg-blue-100 text-blue-800',
			partial: 'bg-yellow-100 text-yellow-800',
			received: 'bg-green-100 text-green-800',
			cancelled: 'bg-red-100 text-red-800'
		};

		return (
			<span className={`px-2 py-1 rounded-full text-xs font-medium ${classes[status] || 'bg-gray-100 text-gray-800'}`}>
                {status.replace('_', ' ')}
            </span>
		);
	};

	if (loading) {
		return (
			<div className="flex justify-center items-center h-64">
				<div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
			</div>
		);
	}

	if (!order) {
		return (
			<div className="container mx-auto px-4 py-6">
				<div className="bg-white rounded-lg shadow p-6 text-center">
					<h2 className="text-xl font-semibold text-gray-800 mb-4">Part Order Not Found</h2>
					<p className="text-gray-600 mb-6">The part order you're looking for does not exist or has been removed.</p>
					<button
						onClick={() => navigate('/part-orders')}
						className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none"
					>
						Return to Order List
					</button>
				</div>
			</div>
		);
	}

	// Get sorted and filtered items
	const sortedAndFilteredItems = getSortedAndFilteredItems();

	// Get filtered receive items
	const filteredReceiveItems = getFilteredReceiveItems();

	return (
		<div className="container mx-auto px-4 py-6">
			{/* Header */}
			<div className="mb-6">
				<div className="flex flex-wrap justify-between items-center">
					<div>
						<div className="flex items-center">
							<button
								onClick={() => navigate('/part-orders')}
								className="mr-4 text-gray-500 hover:text-gray-700"
							>
								<svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
								</svg>
							</button>
							<h1 className="text-2xl font-bold text-gray-800">Part Order #{order.orderNumber}</h1>
						</div>
						<p className="mt-1 text-gray-600">
							Supplier: {order.vendor?.name}
						</p>
					</div>
					<div className="mt-4 sm:mt-0 flex items-center space-x-3">
						<span className="capitalize">{renderStatusBadge(order.status)}</span>
						{order.status === 'draft' && (
							<button
								onClick={() => navigate(`/part-orders/edit/${order.id}`)}
								className="px-3 py-1 bg-white border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50 focus:outline-none"
							>
								Edit
							</button>
						)}
						{/*<div className="relative inline-block text-left">
							<button
								type="button"
								className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 focus:outline-none"
								onClick={() => {
									// Show dropdown or menu of actions
								}}
							>
								Actions
							</button>
						</div>*/}
					</div>
				</div>
			</div>

			{/* Order Details */}
			<div className="bg-white rounded-lg shadow mb-6">
				<div className="p-6">
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
						<div>
							<h3 className="text-sm font-medium text-gray-500">Order Number</h3>
							<p className="mt-1 text-lg text-gray-900">{order.orderNumber}</p>
						</div>
						<div>
							<h3 className="text-sm font-medium text-gray-500">Supplier</h3>
							<p className="mt-1 text-lg text-gray-900">{order.vendor?.name}</p>
						</div>
						<div>
							<h3 className="text-sm font-medium text-gray-500">Status</h3>
							<p className="mt-1">{renderStatusBadge(order.status)}</p>
						</div>
						<div>
							<h3 className="text-sm font-medium text-gray-500">Created By</h3>
							<p className="mt-1 text-lg text-gray-900">{order.createdBy || 'N/A'}</p>
						</div>
						<div>
							<h3 className="text-sm font-medium text-gray-500">Order Date</h3>
							<p className="mt-1 text-lg text-gray-900">
								{order.order_date ? format(new Date(order.order_date), 'MMMM d, yyyy') : 'Not specified'}
							</p>
						</div>
						<div>
							<h3 className="text-sm font-medium text-gray-500">Expected Delivery</h3>
							<p className="mt-1 text-lg text-gray-900">
								{order.expectedDate ? format(new Date(order.expectedDate), 'MMMM d, yyyy') : 'Not specified'}
							</p>
						</div>
						<div>
							<h3 className="text-sm font-medium text-gray-500">Total Value</h3>
							<p className="mt-1 text-lg text-gray-900">
								${order.totalValue ? order.totalValue.toFixed(2) : '0.00'}
							</p>
						</div>
						<div>
							<h3 className="text-sm font-medium text-gray-500">Payment Terms</h3>
							<p className="mt-1 text-lg text-gray-900">{order.paymentTerms || 'N/A'}</p>
						</div>
					</div>

					{order.notes && (
						<div className="mt-6 border-t border-gray-200 pt-6">
							<h3 className="text-lg font-medium text-gray-900">Notes</h3>
							<p className="mt-2 text-gray-700">{order.notes}</p>
						</div>
					)}
				</div>
			</div>

			{/* Order Items */}
			<div className="bg-white rounded-lg shadow mb-6">
				<div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
					<h2 className="text-xl font-semibold text-gray-800">Order Items</h2>
					<div className="flex items-center space-x-3">
						{(order.status === 'ordered' || order.status === 'partially_received') && (
							<button
								onClick={() => setReceiveDialogOpen(true)}
								className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 focus:outline-none"
							>
								Receive Items
							</button>
						)}
						{order.status === 'draft' && (
							<div className={`flex flex-row gap-1`}>
								<button
									onClick={() => {
										setConfirmMessage('Submitting this order will send the purchase order to the supplier for processing.')
										setConfirmAction(() => () => handleStatusChange('ordered'));
										setConfirmDialogOpen(true);
									}}
									className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none"
								>
									Submit Order
								</button>
								<button
									onClick={() => {
										setConfirmAction(() => () => handleStatusChange('cancelled'));
										setConfirmDialogOpen(true);
									}}
									className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none"
								>
									Cancel Order
								</button>
							</div>
						)}
					</div>
				</div>

				{/* Add search box for the main table */}
				<div className="px-6 pt-4 pb-2">
					<div className="relative">
						<input
							type="text"
							className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 pl-10 pr-10"
							placeholder="Search order parts by number or description..."
							value={tableSearchTerm}
							onChange={handleTableSearchChange}
						/>
						<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
							<svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
							</svg>
						</div>
						{tableSearchTerm && (
							<button
								className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
								onClick={clearTableSearch}
							>
								<svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
								</svg>
							</button>
						)}
					</div>
					{tableSearchTerm && (
						<div className="mt-2 text-sm text-gray-500">
							Showing {sortedAndFilteredItems.length} of {order.items.length} items
						</div>
					)}
				</div>

				<div className="p-6">
					{order.items && order.items.length > 0 ? (
						<div className="overflow-x-auto">
							<table className="min-w-full divide-y divide-gray-200">
								<thead className="bg-gray-50">
								<tr>
									<th
										scope="col"
										className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
										onClick={() => requestSort('partNumber')}
									>
										Part Number{getSortIndicator('partNumber')}
									</th>
									<th
										scope="col"
										className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
										onClick={() => requestSort('description')}
									>
										Description{getSortIndicator('description')}
									</th>
									<th
										scope="col"
										className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
										onClick={() => requestSort('quantity')}
									>
										Quantity{getSortIndicator('quantity')}
									</th>
									<th
										scope="col"
										className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
										onClick={() => requestSort('received')}
									>
										Received{getSortIndicator('received')}
									</th>
									<th
										scope="col"
										className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
										onClick={() => requestSort('unit_price')}
									>
										Unit Price{getSortIndicator('unit_price')}
									</th>
									<th
										scope="col"
										className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
										onClick={() => requestSort('total')}
									>
										Total{getSortIndicator('total')}
									</th>
								</tr>
								</thead>
								<tbody className="bg-white divide-y divide-gray-200">
								{sortedAndFilteredItems.length > 0 ? (
									sortedAndFilteredItems.map((item) => (
										<tr key={item.id} className="hover:bg-gray-50">
											<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
												{item.product.sku}-{item.part.partNumber}
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
												{item.part.description}
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
												{item.quantity}
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
												{item.quantity_received || 0}
												{item.quantity_received > 0 && item.quantity_received < item.quantity && (
													<span className="ml-2 px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">
														Partial
													</span>
												)}
												{item.quantity_received >= item.quantity && (
													<span className="ml-2 px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
														Complete
													</span>
												)}
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
												${item.unit_price.toFixed(2)}
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
												${(item.quantity * item.unit_price).toFixed(2)}
											</td>
										</tr>
									))
								) : (
									 <tr>
										 <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
											 {tableSearchTerm
											  ? `No items matching "${tableSearchTerm}"`
											  : "No items in this order"}
										 </td>
									 </tr>
								 )}
								</tbody>
								<tfoot className="bg-gray-50">
								<tr>
									<td colSpan="5" className="px-6 py-4 text-right text-sm font-medium text-gray-500">
										Total:
									</td>
									<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
										${order.totalValue ? order.totalValue.toFixed(2) : '0.00'}
									</td>
								</tr>
								</tfoot>
							</table>
						</div>
					) : (
						 <div className="text-center py-8 text-gray-500">
							 No items in this order
						 </div>
					 )}
				</div>
			</div>

			{/* Action buttons */}
			<div className="flex justify-end space-x-3">
				{order.status === 'draft' && (
					<>
						<button
							onClick={() => {
								setConfirmAction(() => () => handleStatusChange('ordered'));
								setConfirmDialogOpen(true);
							}}
							className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none"
						>
							Submit Order
						</button>
						<button
							onClick={() => {
								setConfirmAction(() => () => handleStatusChange('cancelled'));
								setConfirmDialogOpen(true);
							}}
							className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none"
						>
							Cancel Order
						</button>
					</>
				)}
				{(order.status === 'ordered' || order.status === 'partially_received') && (
					<button
						onClick={() => setReceiveDialogOpen(true)}
						className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none"
					>
						Receive Items
					</button>
				)}
			</div>

			{/* Receive Items Dialog */}
			{receiveDialogOpen && (
				<div className="fixed z-10 inset-0 overflow-y-auto">
					<div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
						<div className="fixed inset-0 transition-opacity" aria-hidden="true">
							<div className="absolute inset-0 bg-gray-500 opacity-75"></div>
						</div>

						<span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

						<div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
							<div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
								<div className="sm:flex sm:items-start">
									<div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
										<h3 className="text-lg leading-6 font-medium text-gray-900">
											Receive Items
										</h3>
										<div className="mt-2">
											<p className="text-sm text-gray-500">
												Enter the quantities for items being received.
											</p>
										</div>

										{/* Search input */}
										<div className="mt-4 mb-4">
											<div className="relative">
												<input
													type="text"
													className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 pl-10 pr-10"
													placeholder="Search parts by number or description..."
													value={searchTerm}
													onChange={handleSearchChange}
												/>
												<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
													<svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
													</svg>
												</div>
												{searchTerm && (
													<button
														className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
														onClick={clearSearch}
													>
														<svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
														</svg>
													</button>
												)}
											</div>
										</div>

										<div className="flex justify-between mb-4">
											<div className="text-sm text-gray-500">
												{filteredReceiveItems.length} of {receiveItems.length} items
												{searchTerm && ` matching "${searchTerm}"`}
											</div>
											<button
												type="button"
												className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800 focus:outline-none"
												onClick={receiveAll}
											>
												Receive All Remaining
											</button>
										</div>

										<div className="max-h-96 overflow-y-auto">
											{filteredReceiveItems.length > 0 ? (
												filteredReceiveItems.map((item) => (
													<div key={item.id + item.part.partNumber} className="mb-4 p-4 border border-gray-200 rounded-md">
														<div className="flex justify-between mb-2">
															<div>
																<div className="font-medium">{item.product.sku}-{item.part.partNumber}</div>
																<div className="text-sm text-gray-500">{item.part.description}</div>
															</div>
															<div className="text-sm text-gray-500">
																Received: {item.quantity_received} / {item.quantity_ordered}
															</div>
														</div>

														<div className="flex items-center">
															<label htmlFor={`quantity-${item.id}`} className="block text-sm font-medium text-gray-700 mr-2">
																Quantity to Receive:
															</label>
															<input
																type="number"
																id={`quantity-${item.id}`}
																min="0"
																max={item.quantity_ordered - item.quantity_received}
																className="mt-1 block w-24 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
																value={item.quantity === 0 && item.tempEmpty ? '' : item.quantity}
																onChange={(e) => {
																	const value = e.target.value;
																	if (value === '') {
																		// Allow empty input temporarily
																		setReceiveItems(prev =>
																			                prev.map(prevItem =>
																				                         prevItem.id === item.id
																				                         ? { ...prevItem, quantity: 0, tempEmpty: true }
																				                         : prevItem
																			                )
																		);
																	} else {
																		// Normal processing with numeric value
																		handleReceiveQuantityChange(item.id, parseInt(value) || 0);
																	}
																}}
																onBlur={() => {
																	// Clear temporary empty state on blur
																	setReceiveItems(prev =>
																		                prev.map(prevItem =>
																			                         prevItem.id === item.id
																			                         ? { ...prevItem, tempEmpty: false }
																			                         : prevItem
																		                )
																	);
																}}
																disabled={item.quantity_received >= item.quantity_ordered}
															/>
														</div>
													</div>
												))
											) : (
												 <div className="text-center py-8 text-gray-500">
													 {searchTerm ? 'No items match your search.' : 'No items to receive.'}
												 </div>
											 )}
										</div>
									</div>
								</div>
							</div>
							<div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
								<button
									type="button"
									className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm"
									onClick={handleReceiveItems}
								>
									Receive Items
								</button>
								<button
									type="button"
									className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
									onClick={() => {
										setReceiveDialogOpen(false);
										setSearchTerm(''); // Clear search when closing
									}}
								>
									Cancel
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Confirmation Dialog */}
			{confirmDialogOpen && (
				<div className="fixed z-10 inset-0 overflow-y-auto">
					<div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
						<div className="fixed inset-0 transition-opacity" aria-hidden="true">
							<div className="absolute inset-0 bg-gray-500 opacity-75"></div>
						</div>

						<span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

						<div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
							<div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
								<div className="sm:flex sm:items-start">
									<div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
										<svg className="h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
										</svg>
									</div>
									<div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
										<h3 className="text-lg leading-6 font-medium text-gray-900">
											Confirm Action
										</h3>
										<div className="mt-2">
											<p className="text-sm text-gray-500">
												Are you sure you want to proceed with this action? This cannot be undone.
											</p>
											{confirmMessage && (
												<p className="mt-2 text-sm text-red-500 font-bold">{confirmMessage}</p>
											)}
										</div>
									</div>
								</div>
							</div>
							<div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
								<button
									type="button"
									className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
									onClick={confirmAction}
								>
									Confirm
								</button>
								<button
									type="button"
									className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
									onClick={() => {
										setConfirmMessage(null);
										setConfirmDialogOpen(false);
									}}>
									Cancel
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default PartOrderDetails;
